import {
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Typography,
} from '@mui/material'
import {
  Box,
  DatePicker,
  FieldTitle,
  LabraToolTip,
  labraUIDateFormat,
  monthYearWithComaFormat,
} from '@labrav/react-components'
import { InfoOutlined } from '@mui/icons-material'
import { useStyles } from './CustomerTerm.styles'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import { getMinMaxDateAzureOffer } from '../../../modules/privateOffer/privateOfferCreation/utils/dateHelpers'
import type {
  KeyValue,
  OffersWithErrors,
} from '../../../modules/privateOffer/privateOfferCreation/types'
import { isEmpty } from 'lodash'
import React, { useCallback } from 'react'
import clsx from 'clsx'
import { useTimeZone } from '../../../common/utils/hooks/useTimeZone'
import { useCommonStyles } from '../../../common/styles/common.styles'
import {
  azureOfferExpirationDateField,
  endDateField,
  specifiedMonthField,
  startDateTypeField,
} from './fields'
import {
  formatAzureOfferEndDate,
  formatAzureOfferStartDate,
  getStartDateTypeValue,
  getStartDateTypeValueToDisplay,
} from './utils'

export type CustomerTermProps = {
  privateOfferWithError: OffersWithErrors
  handleDetailsChange: (data: KeyValue) => void
}

export const CustomerTerm = React.memo(
  ({ privateOfferWithError, handleDetailsChange }: CustomerTermProps) => {
    const { classes } = useStyles()
    const { classes: commonClasses } = useCommonStyles()
    const { errors } = privateOfferWithError
    const { timeZone } = useTimeZone()

    const { minDate, maxDate, minEndDate, maxEndDate } =
      getMinMaxDateAzureOffer({ timeZone })

    const specifiedMonthFieldValue = {
      ...specifiedMonthField,
      defaultValue: !isEmpty(privateOfferWithError?.offerStartDate)
        ? privateOfferWithError?.offerStartDate
        : null,
      minDate,
      maxDate,
    }

    const startDateTypeFieldValue = {
      ...startDateTypeField,
      value: getStartDateTypeValueToDisplay(
        privateOfferWithError?.variableStartDate
      ),
    }

    const endDateFieldValue = {
      ...endDateField,
      defaultValue: !isEmpty(privateOfferWithError?.offerExpirationDate)
        ? privateOfferWithError?.offerExpirationDate
        : null,
      minDate: minEndDate,
      maxDate: maxEndDate,
    }

    const azureOfferExpirationDateFieldValue = {
      ...azureOfferExpirationDateField,
      defaultValue: !isEmpty(privateOfferWithError?.acceptBy)
        ? privateOfferWithError?.acceptBy
        : null,
      minDate: 'today',
      maxDate,
    }

    const handleAzureDateChange = (date: string | null, keyName: string) => {
      handleDetailsChange({
        key: keyName,
        value: date || '',
      })
    }

    const handleInputChangeSelect = useCallback((event: unknown) => {
      const { name, value } = (event as React.ChangeEvent<HTMLInputElement>)
        .target
      const actualValue = getStartDateTypeValue(value)
      handleDetailsChange({
        key: name as string,
        value: actualValue,
      })
    }, [])

    const handleStartDateChange = useCallback(
      (date: string | null) => {
        handleDetailsChange({
          key: 'offerStartDate',
          value: formatAzureOfferStartDate(date),
        })
      },
      [privateOfferWithError.offerStartDate]
    )

    const handleEndDateChange = useCallback(
      (date: string | null) => {
        handleDetailsChange({
          key: 'offerExpirationDate',
          value: formatAzureOfferEndDate(date),
        })
      },
      [privateOfferWithError.offerExpirationDate]
    )

    return (
      <div>
        <Box heading={'Customer Terms'} subHeading={''}>
          <div
            className={clsx(
              classes.subHeadingContainer,
              classes.companyDetailSubHead
            )}
          >
            <Typography
              className={classes.subHeading}
              data-testid="start-date-heading"
            >
              Start date
            </Typography>
            <LabraToolTip
              tooltipPlacement={'right'}
              label={
                'Select accepted date if you want the private offer to start as soon as your customer accepts it or select a specific month in the future.'
              }
            >
              <InfoOutlined className={classes.infoIcon} />
            </LabraToolTip>
          </div>

          <Grid container className={classes.textAlign} gap={4} mb={4}>
            <Grid
              size={{
                xs: 12,
                sm: 6,
                md: 4,
                lg: 3,
                xl: 3,
              }}
            >
              <FormControl className={classes.selectFormWidth}>
                <InputLabel
                  id="start-date-type-label"
                  className={classes.inputLabel}
                  data-testid={'select-label-variableStartDate'}
                  classes={{ shrink: commonClasses.shrink }}
                >
                  {startDateTypeFieldValue.title}{' '}
                  <FieldTitle title={''} isRequired={true} />
                </InputLabel>
                <Select
                  labelId={startDateTypeFieldValue.id}
                  name={startDateTypeFieldValue.id}
                  value={startDateTypeFieldValue?.value}
                  onChange={handleInputChangeSelect}
                  label={startDateTypeFieldValue.title}
                  inputProps={{ 'data-testid': 'input' }}
                  variant="standard"
                  className={classes.selectField}
                  IconComponent={props => (
                    <KeyboardArrowDownIcon
                      {...props}
                      fontSize="small"
                      data-testid="arrow-button"
                    />
                  )}
                  classes={{ icon: classes.icon }}
                  data-testid="start-date-selection"
                >
                  {(startDateTypeFieldValue.options || []).map(menuOption => (
                    <MenuItem
                      data-testid="menu-item"
                      value={menuOption.value}
                      className={classes.menuRoot}
                      key={menuOption.value}
                    >
                      {menuOption.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            {privateOfferWithError?.variableStartDate === false ? (
              <Grid
                size={{
                  xs: 12,
                  sm: 6,
                  md: 4,
                  lg: 3,
                  xl: 3,
                }}
              >
                <DatePicker
                  format={monthYearWithComaFormat}
                  field={specifiedMonthFieldValue}
                  key={'offerStartDate'}
                  errorHelperText={(errors?.offerStartDate as string) || ''}
                  views={['year', 'month']}
                  handleDateChange={handleStartDateChange}
                  customClasses={clsx(classes.textField, classes.dateField)}
                />
              </Grid>
            ) : (
              <></>
            )}
          </Grid>
          <Grid
            container
            gap={4}
            className={clsx(classes.textAlign, classes.gridItem)}
          >
            <Grid
              size={{
                xs: 12,
                sm: 6,
                md: 4,
                lg: 3,
                xl: 3,
              }}
            >
              <div
                className={clsx(
                  classes.subHeadingContainer,
                  classes.companyDetailSubHead
                )}
              >
                <Typography
                  className={classes.subHeading}
                  data-testid="end-date-heading"
                >
                  End date
                </Typography>
                <LabraToolTip
                  tooltipPlacement={'right'}
                  label={
                    'Select the end date for your private offer. The specified price and terms will not be applicable after the end date.'
                  }
                >
                  <InfoOutlined className={classes.infoIcon} />
                </LabraToolTip>
              </div>
              <DatePicker
                format={monthYearWithComaFormat}
                field={endDateFieldValue}
                key={'offerExpirationDate'}
                errorHelperText={(errors?.offerExpirationDate as string) || ''}
                views={['year', 'month']}
                handleDateChange={handleEndDateChange}
                customClasses={clsx(classes.textField, classes.dateField)}
              />
            </Grid>
            <Grid
              size={{
                xs: 12,
                sm: 6,
                md: 4,
                lg: 3,
                xl: 3,
              }}
            >
              <div
                className={clsx(
                  classes.subHeadingContainer,
                  classes.companyDetailSubHead
                )}
              >
                <Typography
                  className={classes.subHeading}
                  data-testid="acceptBy-date-heading"
                >
                  Offer expiration date
                </Typography>
                <LabraToolTip
                  tooltipPlacement={'right'}
                  label={
                    'Select the offer expiration date for your private offer. Your offer will expire after this date and your customer will not be able to accept it.'
                  }
                >
                  <InfoOutlined className={classes.infoIcon} />
                </LabraToolTip>
              </div>
              <DatePicker
                format={labraUIDateFormat}
                field={azureOfferExpirationDateFieldValue}
                key={'acceptBy'}
                errorHelperText={(errors?.acceptBy as string) || ''}
                handleDateChange={(date: string | null) =>
                  handleAzureDateChange(date, 'acceptBy')
                }
                customClasses={clsx(classes.textField, classes.dateField)}
              />
            </Grid>
          </Grid>
        </Box>
      </div>
    )
  }
)
